/* eslint-disable @typescript-eslint/no-unused-vars */

import React, {
  Fragment,
  FC,
  cloneElement,
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  List,
  TextField,
  EditButton,
  Filter,
  TextInput,
  SelectInput,
  TopToolbar,
  sanitizeListRestProps,
  useListContext,
  CreateButton,
  ExportButton,
  FilterProps,
  ListProps,
  ToolbarProps,
  ExportButtonProps,
  ShowButton,
  useNotify,
  useRefresh,
  useUnselectAll,
  useDataProvider,
  FunctionField,
} from 'react-admin';
import Datagrid from 'components/Datagrid';
import { SubdivisionOverrideTypeEnum } from '@vatos-pas/common';
import { exporter } from 'providers/export';
import { CAN_EDIT_SUB_DIVISION } from 'providers/permissions';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { SupervisorField } from 'components/SupervisorListItems';
import { SupervisorsModal } from '../components/SupervisorsModal';
import { PricingModal } from '../components/PricingModal';
import { PricingIconButton } from '../components/PricingIconButton';
import { LazyReferenceInput } from 'components/LazyAutoCompleteInput';
import { SupervisorReferenceInput } from 'components/SupervisorReferenceInput';
import Button from '@material-ui/core/Button';
import { Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { trackEvent } from 'utils/mixpanel';

const SubDivisionFilter: FC<FilterProps> = props => (
  <Filter {...props}>
    <TextInput
      label="Name"
      source="name"
      data-cy="subdivisionFilterName"
      alwaysOn
    />
    <TextInput
      label="Address"
      source="address1"
      data-cy="subdivisionFilterAddress"
    />
    <TextInput label="City" source="city" data-cy="subdivisionFilterCity" />
    <TextInput label="State" source="state" data-cy="subdivisionFilterState" />
    <TextInput label="Zip" source="zip" data-cy="subdivisionFilterZip" />
    <LazyReferenceInput
      label="Builder"
      source="builderId"
      reference="builder"
      filterToQuery={value => (value ? { name: value } : {})}
      sort={{ field: 'name', order: 'ASC' }}
      optionText="name"
      optionValue="id"
      allowEmpty={false}
      suggestionLimit={5}
      alwaysOn
    />
    <LazyReferenceInput
      label="Region"
      source="regionId"
      data-cy="subdivisionFilterRegion"
      reference="region"
      filterToQuery={value => (value ? { name: value } : {})}
      sort={{ field: 'name', order: 'ASC' }}
      optionText="name"
      optionValue="id"
      alwaysOn
    />
    <SupervisorReferenceInput
      source="supervisorUserIds||$cont"
      activeOnly
      resettable
      alwaysOn
      allowEmpty={false}
      data-cy="subdivisionFilterSupervisor"
    />
    <SelectInput
      source="warrantyStatus||$eq"
      label="Warranty Status"
      data-cy="subdivisionFilterWarrantyStatus"
      choices={[
        { val: 'Production', text: 'Production' },
        { val: 'Warranty', text: 'Warranty' },
      ]}
      emptyText="All"
      optionValue="val"
      optionText="text"
    />
    <SelectInput
      source="phaseStructureType||$eq"
      label="Workflow"
      data-cy="subdivisionFilterWorkflow"
      choices={[
        { val: 'DrywallAndPaint', text: 'Drywall and Paint' },
        { val: 'Drywall', text: 'Drywall' },
      ]}
      emptyText="All"
      optionValue="val"
      optionText="text"
    />
    <SelectInput
      source="overrideRegionCostPerBoard||$eq"
      label="Override"
      data-cy="subdivisionFilterOverride"
      choices={[
        { val: 'true', text: 'Active' },
        { val: 'false', text: 'Inactive' },
      ]}
      emptyText="All"
      optionValue="val"
      optionText="text"
      alwaysOn
    />
  </Filter>
);

const BullkActions: FC<any> = props => {
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll(props.resource);
  const dataProvider = useDataProvider();
  const { data, selectedIds } = useListContext(props);
  const [showSupervisorsModal, setShowSupervisorsModal] = useState(false);
  const [diffRegions, setDiffRegions] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('');

  useEffect(() => {
    if (!selectedIds.length) {
      setDiffRegions(false);
      return;
    }

    const firstRegion = data[selectedIds[0]]?.regionId;
    const hasDifferentRegion = selectedIds.some(
      id => data[id]?.regionId !== firstRegion,
    );

    if (!hasDifferentRegion) {
      setSelectedRegion(firstRegion);
    } else {
      setSelectedRegion('');
    }
    setDiffRegions(hasDifferentRegion);
  }, [selectedIds, data]);

  const openSupervisorsModal = () => {
    if (diffRegions) {
      return notify('Please select only one Region at a time.', 'error');
    } else {
      setShowSupervisorsModal(true);
    }
  };

  return (
    <>
      <Button onClick={openSupervisorsModal}>Change Supervisor</Button>
      <SupervisorsModal
        open={showSupervisorsModal}
        onClose={() => setShowSupervisorsModal(false)}
        regionId={selectedRegion}
        dataProvider={dataProvider}
        unselectAll={unselectAll}
        refresh={refresh}
        selectedIds={selectedIds}
        notify={notify}
      />
    </>
  );
};

type ListActionProps = ToolbarProps &
  Pick<ExportButtonProps, 'maxResults'> & {
    filters?: ReactElement;
  };

const ListActions: FC<ListActionProps> = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    basePath,
    showFilter,
    total,
  } = useListContext();

  const { hasPermission } = PermissionsProvider.useContainer();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      {hasPermission(CAN_EDIT_SUB_DIVISION) ? (
        <CreateButton basePath={basePath} />
      ) : (
        <Fragment />
      )}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

const WorkFlowComponent = ({ record }: any) => {
  return (
    <Typography>
      {record.phaseStructureType === 'Drywall'
        ? 'Drywall'
        : 'Drywall and Paint'}
    </Typography>
  );
};

const OverrideField: FC<any> = props => (
  <FunctionField
    {...props}
    render={(record: any) =>
      record?.overrideRegionCostPerBoard ? <CheckIcon /> : null
    }
  />
);

const ProductionPricingField: FC<any> = props => (
  <FunctionField
    {...props}
    render={(record: any) => {
      if (!record?.overrideRegionCostPerBoard) return null;
      if (record?.overrideType === SubdivisionOverrideTypeEnum.FIXED)
        return 'Fixed';
      if (record?.overrideType === SubdivisionOverrideTypeEnum.OFFSET)
        return 'Offset';
      return null;
    }}
  />
);

const LastUpdateField: FC<any> = props => (
  <FunctionField
    {...props}
    render={(record: any) => {
      if (!record?.updatedAt) return null;
      const date = new Date(record.updatedAt);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }}
  />
);

export const SubDivisionsList: FC<ListProps> = props => {
  const { hasPermission } = PermissionsProvider.useContainer();
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [selectedSubdivisionId, setSelectedSubdivisionId] = useState<
    string | null
  >(null);

  const handleOpenPricingModal = useCallback((subdivisionId: string) => {
    setSelectedSubdivisionId(subdivisionId);
    setPricingModalOpen(true);
  }, []);

  const handleClosePricingModal = useCallback(() => {
    setPricingModalOpen(false);
    setSelectedSubdivisionId(null);
  }, []);

  useEffect(() => {
    trackEvent('PageView', {
      resource: 'subdivision',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);
  return (
    <>
      <List
        {...props}
        exporter={exporter('subdivisions')}
        bulkActionButtons={<BullkActions />}
        filters={<SubDivisionFilter>{props.filters}</SubDivisionFilter>}
        filter={{
          $join: [
            // { field: 'supervisorUser', select: ['id', 'firstName', 'lastName'] },
            { field: 'builder', select: ['id', 'name'] },
            { field: 'region', select: ['id', 'name'] },
          ],
        }}
        sort={{ field: 'builderId', order: 'ASC' }}
        actions={<ListActions />}
      >
        <Datagrid
          rowClick={() =>
            hasPermission(CAN_EDIT_SUB_DIVISION) ? 'edit' : 'show'
          }
        >
          <TextField label="Builder" source="builderName" />
          <TextField label="Region" source="regionName" />
          <WorkFlowComponent label="Workflow" source="phaseStructureType" />
          <SupervisorField label="Supervisor" />
          <TextField source="name" />
          <TextField source="address1" label="Address" />
          <TextField source="city" />
          <TextField source="state" />
          <TextField source="zip" />
          <OverrideField
            label="Override"
            source="overrideRegionCostPerBoard"
            data-cy="subdivisionColumnOverride"
          />
          <ProductionPricingField
            label="Production Pricing"
            source="overrideType"
            data-cy="subdivisionColumnProductionPricing"
          />
          <FunctionField
            label=""
            render={(record: any) => (
              <PricingIconButton
                record={record}
                onClick={handleOpenPricingModal}
              />
            )}
          />
          <LastUpdateField
            label="Last Update"
            source="updatedAt"
            data-cy="subdivisionColumnLastUpdate"
          />
          <TextField source="warrantyStatus" label="Warranty Status" />
          {hasPermission(CAN_EDIT_SUB_DIVISION) ? (
            <EditButton />
          ) : (
            <ShowButton />
          )}
        </Datagrid>
      </List>
      <PricingModal
        open={pricingModalOpen}
        onClose={handleClosePricingModal}
        subdivisionId={selectedSubdivisionId}
      />
    </>
  );
};
